import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { displayDate, getDateFromDateString } from "../../../utils/dataUtils";

function ReviewAndClean({ parsedData, fieldMapping, onCleaned }) {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingRow, setEditingRow] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const formatDisplayValue = useCallback((value) => {
    if (value instanceof Date) return displayDate(value);
    return value;
  }, []);

  const formatEditValue = useCallback((value) => {
    if (value instanceof Date) {
      return Number.isFinite(value.getTime())
        ? value.toISOString().slice(0, 10)
        : "";
    }
    return value ?? "";
  }, []);

  const validateRow = useCallback((row) => {
    const errors = [];
    // Check required fields
    if (!row.firstName || row.firstName.trim() === "") {
      errors.push("Vorname fehlt");
    }
    if (!row.lastName || row.lastName.trim() === "") {
      errors.push("Nachname fehlt");
    }
    return errors;
  }, []);

  const isValidDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  }, []);

  const checkWarnings = useCallback(
    (row) => {
      const warnings = [];
      // Check for potential issues
      if (row.email && !row.email.includes("@")) {
        warnings.push("E-Mail ungültig");
      }
      if (row.iban && row.iban.length < 15) {
        warnings.push("IBAN möglicherweise ungültig");
      }
      if (row.birthdate && !isValidDate(row.birthdate)) {
        warnings.push("Datum ungültig");
      }
      return warnings;
    },
    [isValidDate],
  );

  const calculateIssues = useCallback((dataRows) => {
    const allErrors = dataRows.filter(
      (row) => row._errors && row._errors.length > 0,
    );
    const allWarnings = dataRows.filter(
      (row) => row._warnings && row._warnings.length > 0,
    );
    setErrors(allErrors);
    setWarnings(allWarnings);
  }, []);

  useEffect(() => {
    // Transform parsed data into editable format
    let sourceRows = [];
    let contractByMemberId = new Map();

    if (Array.isArray(parsedData?.vertragsnehmer)) {
      parsedData.vertragsnehmer.forEach((contract) => {
        const members = Array.isArray(contract?.hasMembers)
          ? contract.hasMembers
          : [];
        members.forEach((memberId) => {
          if (!memberId) return;
          contractByMemberId.set(String(memberId), contract);
        });
      });
    }

    // Handle different data sources
    if (
      fieldMapping?.sourceType === "json" &&
      Array.isArray(fieldMapping?.transformedMembers)
    ) {
      // JSON import: use pre-extracted members
      sourceRows = fieldMapping.transformedMembers;
    } else if (Array.isArray(parsedData?.mitglieder)) {
      // Fallback: use JSON members directly if mapping is missing
      sourceRows = parsedData.mitglieder;
    } else if (Array.isArray(parsedData?.rows)) {
      // CSV import: use parsed rows
      sourceRows = parsedData.rows;
    }

    if (sourceRows.length > 0) {
      const hasMemberMapping =
        fieldMapping?.members && Object.keys(fieldMapping.members).length > 0;
      const hasContractMapping =
        fieldMapping?.contracts &&
        Object.keys(fieldMapping.contracts).length > 0;
      const dateFields = new Set([
        "birthdate",
        "contractDate",
        "terminationDate",
        "beginningDate",
        "lastTesting",
      ]);

      const transformedData = sourceRows.map((row, index) => {
        // Apply field mapping to transform CSV columns to database fields
        let mappedRow = {};

        if (hasMemberMapping) {
          // Map CSV columns to member fields
          Object.keys(fieldMapping.members).forEach((fieldKey) => {
            const columnName = fieldMapping.members[fieldKey];
            if (columnName && row[columnName] !== undefined) {
              mappedRow[fieldKey] = row[columnName];
            }
          });
        }

        if (hasContractMapping) {
          // Map CSV columns to contract fields
          Object.keys(fieldMapping.contracts).forEach((fieldKey) => {
            const columnName = fieldMapping.contracts[fieldKey];
            if (columnName && row[columnName] !== undefined) {
              mappedRow[fieldKey] = row[columnName];
            }
          });
        }

        if (!hasMemberMapping && !hasContractMapping) {
          // If no mapping (JSON case), use row as-is
          mappedRow = { ...row };

          const memberId = row?.mitgliedId || row?.memberId || row?.id;
          const contract = memberId
            ? contractByMemberId.get(String(memberId))
            : null;

          if (contract) {
            mappedRow.owner = mappedRow.owner || contract.owner || "";
            mappedRow.iban = mappedRow.iban || contract.iban || "";
            mappedRow.bic = mappedRow.bic || contract.bic || "";
            mappedRow.bank = mappedRow.bank || contract.bank || "";
            mappedRow.contractFirstName =
              mappedRow.contractFirstName || contract.firstName || "";
            mappedRow.contractLastName =
              mappedRow.contractLastName || contract.lastName || "";
            mappedRow.contractTitle =
              mappedRow.contractTitle || contract.title || "";

            if (!mappedRow.email && contract.email) {
              mappedRow.email = contract.email;
            }
            if (!mappedRow.phone && contract.phoneNumber) {
              mappedRow.phone = contract.phoneNumber;
            }
            if (!mappedRow.mobile && contract.mobile) {
              mappedRow.mobile = contract.mobile;
            }
            if (!mappedRow.street && contract.street) {
              mappedRow.street = contract.street;
            }
            if (!mappedRow.zip && contract.zip) {
              mappedRow.zip = contract.zip;
            }
            if (!mappedRow.city && contract.city) {
              mappedRow.city = contract.city;
            }
          }
        }

        // Normalize date fields to Date objects for consistent import
        Object.keys(mappedRow).forEach((key) => {
          if (!dateFields.has(key)) return;
          const value = mappedRow[key];
          if (value == null || value === "") return;
          mappedRow[key] = getDateFromDateString(value);
        });

        return {
          id: index,
          sourceIndex: index,
          ...mappedRow,
          _errors: validateRow(mappedRow),
          _warnings: checkWarnings(mappedRow),
        };
      });
      setData(transformedData);
      calculateIssues(transformedData);
    } else {
      setData([]);
      calculateIssues([]);
    }
  }, [
    calculateIssues,
    checkWarnings,
    fieldMapping,
    parsedData,
    validateRow,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditRow = (row) => {
    setEditingRow({ ...row });
    setEditDialogOpen(true);
  };

  const handleDeleteRow = (rowId) => {
    setData((prev) => {
      const newData = prev.filter((row) => row.id !== rowId);
      calculateIssues(newData);
      return newData;
    });
  };

  const handleSaveEdit = () => {
    setData((prev) => {
      const newData = prev.map((row) =>
        row.id === editingRow.id
          ? {
              ...editingRow,
              _errors: validateRow(editingRow),
              _warnings: checkWarnings(editingRow),
            }
          : row,
      );
      calculateIssues(newData);
      return newData;
    });
    setEditDialogOpen(false);
    setEditingRow(null);
  };

  const handleContinue = () => {
    // Clean up internal properties before passing to next step
    const dateFields = new Set([
      "birthdate",
      "contractDate",
      "terminationDate",
      "beginningDate",
      "lastTesting",
    ]);

    const cleanedData = data.map((row) => {
      const { id, _errors, _warnings, ...cleanRow } = row;
      Object.keys(cleanRow).forEach((key) => {
        if (!dateFields.has(key)) return;
        const value = cleanRow[key];
        if (value instanceof Date) return;
        if (value == null || value === "") return;
        cleanRow[key] = getDateFromDateString(value);
      });
      return cleanRow;
    });
    onCleaned(cleanedData);
  };

  const hasIban = data.some((row) => row.iban);
  const displayColumns = [
    "firstName",
    "lastName",
    "birthdate",
    "memberNumber",
    "email",
    ...(hasIban ? ["iban"] : []),
  ];

  const ibanGroups = data.reduce((acc, row) => {
    const raw = row.iban ? String(row.iban).replace(/\s+/g, "") : "";
    if (!raw) return acc;
    const key = raw.toUpperCase();
    acc[key] = acc[key] || { iban: raw, count: 0, members: [] };
    acc[key].count += 1;
    acc[key].members.push(`${row.firstName || ""} ${row.lastName || ""}`.trim());
    return acc;
  }, {});
  const ibanGroupList = Object.values(ibanGroups).sort(
    (a, b) => b.count - a.count,
  );

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        Daten überprüfen und bereinigen
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        paragraph
      >
        Überprüfen Sie Ihre Daten und beheben Sie eventuelle Fehler
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Paper elevation={1} sx={{ p: 2, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon icon="mdi:table-row" width={24} color="primary" />
            <Box>
              <Typography variant="h4">{data.length}</Typography>
              <Typography variant="caption" color="text.secondary">
                Datensätze
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            bgcolor: errors.length > 0 ? "#fee" : "inherit",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon
              icon="mdi:alert-circle"
              width={24}
              style={{ color: errors.length > 0 ? "#d32f2f" : "#666" }}
            />
            <Box>
              <Typography
                variant="h4"
                color={errors.length > 0 ? "error" : "inherit"}
              >
                {errors.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Fehler
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            p: 2,
            flex: 1,
            bgcolor: warnings.length > 0 ? "#fff4e5" : "inherit",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon
              icon="mdi:alert"
              width={24}
              style={{ color: warnings.length > 0 ? "#ed6c02" : "#666" }}
            />
            <Box>
              <Typography
                variant="h4"
                color={warnings.length > 0 ? "warning.main" : "inherit"}
              >
                {warnings.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Warnungen
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{errors.length} Datensätze</strong> enthalten Fehler und
            müssen korrigiert werden, bevor der Import fortgesetzt werden kann.
          </Typography>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{warnings.length} Datensätze</strong> enthalten Warnungen.
            Sie können dennoch importiert werden, überprüfen Sie diese aber
            sicherheitshalber.
          </Typography>
        </Alert>
      )}

      {data.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Es wurden keine Datensätze gefunden. Bitte prüfen Sie die
            Dateistruktur oder gehen Sie einen Schritt zurück.
          </Typography>
        </Alert>
      )}

      {hasIban && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Vertragsverbindungen nach IBAN
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Mitglieder mit derselben IBAN werden zu einem gemeinsamen Vertrag
            zusammengeführt. Sie können die IBANs bei Bedarf im Datensatz
            bearbeiten.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {ibanGroupList.map((group) => (
              <Accordion
                key={group.iban}
                disableGutters
                elevation={0}
                sx={{ border: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}
              >
                <AccordionSummary
                  expandIcon={<Icon icon="mdi:chevron-down" width={20} />}
                  sx={{ minHeight: 48, px: 2 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                    <Icon
                      icon="mdi:file-document-outline"
                      width={20}
                      style={{ color: group.count > 1 ? "#2e7d32" : "#9e9e9e" }}
                    />
                    <Typography variant="body2" fontWeight="bold" sx={{ flexGrow: 1 }}>
                      {group.iban}
                    </Typography>
                    <Chip
                      label={`${group.count} ${group.count === 1 ? "Mitglied" : "Mitglieder"}`}
                      size="small"
                      color={group.count > 1 ? "success" : "default"}
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
                  {group.members.map((name, idx) => (
                    <Typography key={idx} variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                      {name}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.lighter" }}>
              <TableCell>Status</TableCell>
              {displayColumns.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor:
                      row._errors?.length > 0
                        ? "#fee"
                        : row._warnings?.length > 0
                        ? "#fff4e5"
                        : "inherit",
                  }}
                >
                  <TableCell>
                    {row._errors?.length > 0 ? (
                      <Chip
                        icon={<Icon icon="mdi:alert-circle" />}
                        label="Fehler"
                        size="small"
                        color="error"
                      />
                    ) : row._warnings?.length > 0 ? (
                      <Chip
                        icon={<Icon icon="mdi:alert" />}
                        label="Warnung"
                        size="small"
                        color="warning"
                      />
                    ) : (
                      <Chip
                        icon={<Icon icon="mdi:check" />}
                        label="OK"
                        size="small"
                        color="success"
                      />
                    )}
                  </TableCell>
                  {displayColumns.map((col) => (
                    <TableCell key={col}>
                      {formatDisplayValue(row[col]) || "-"}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEditRow(row)}>
                      <Icon icon="mdi:pencil" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteRow(row.id)}
                    >
                      <Icon icon="mdi:delete" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={errors.length > 0 || data.length === 0}
          endIcon={<Icon icon="mdi:arrow-right" />}
        >
          Weiter zur Validierung
        </Button>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Datensatz bearbeiten</DialogTitle>
        <DialogContent>
          {editingRow && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              {Object.keys(editingRow)
                .filter(
                  (key) =>
                    !key.startsWith("_") &&
                    key !== "id" &&
                    key !== "sourceIndex",
                )
                .map((key) => (
                  <TextField
                    key={key}
                    label={key}
                    value={formatEditValue(editingRow[key])}
                    onChange={(e) =>
                      setEditingRow((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                  />
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ReviewAndClean.propTypes = {
  parsedData: PropTypes.object.isRequired,
  fieldMapping: PropTypes.object.isRequired,
  onCleaned: PropTypes.func.isRequired,
};

export default ReviewAndClean;
